// https://github.com/photolane/parabo-next/blob/e52242f29c111d82078a85a1c6dc252fc6e761c0/_templates/product.html
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import queryString from 'utils/queryString';
import productStillImages from 'utils/productStillImages';
import { Alert, Button, Grid, Typography } from 'components/base';
import {
  HtmlContent,
  ProductDetailCarousel,
  ScriptMarkupBreadcrumb,
  ScriptMarkupFaq,
  ScriptMarkupProduct,
  faqContentToItems,
  getCloudinaryUrl,
} from 'components/rich';
import useDataContext from 'data/context/data';
import ProductPrice from '../../components/custom/productPrice';
import useCartContext from 'ecommerce/context/cart';
import useClaimedCoupon from 'ecommerce/context/claimedCoupon';
import { couponPricePreview } from 'ecommerce/lib/coupon';
import ProductConfiguration, { prepareBundleData } from 'ecommerce/components/productConfiguration';
import { getInitialPriceData } from 'ecommerce/lib/productConfiguration';
import useEditorContext from 'editor/context/editorContext';

import { EditorPageLink, CartPageLink } from '../../components/pageLinks';
import LegacyProductSections from '../../components/product/legacyProductSections';
import ProductSections from '../../components/product/productSections';
import UnavailableNotice from '../../components/product/unavailableNotice';
import { Head, Layout } from '../../components/layout';
import SETTINGS from '../../settings';

import tracker from '../../lib/tracker';
import { usesProductPageLayout } from '../../lib/productPage.mjs';

/**
 * Product has many skuProducts. In case of bundle, there is also secondaryProduct.
 * By default is secondaryProductIndex as -1 - that means "Print Only" version
 */
const ProductDetailPage = props => {
  const cartContext = useCartContext();
  const claimedCoupon = useClaimedCoupon();
  const editorContext = useEditorContext();
  const dataContext = useDataContext();

  const product = dataContext.getProduct(props.productId);
  const defaultPrintOnly = product.config.defaultPrintOnly ?? false;

  /**
   * Configuration { product, primaryProductData, secondaryProductData, price, bundle }
   */
  const [configuration, setConfiguration] = useState(() =>
    getInitialPriceData(product, dataContext, defaultPrintOnly),
  );
  // The picker's initial SKU (config.defaultSku or the first by position), so bundles match it
  const [skuProduct, setSkuProduct] = useState(configuration.product);

  // Reset during render: a reset effect would run after the picker's mount onChange and wipe it
  const [shownProduct, setShownProduct] = useState(product);
  if (shownProduct !== product) {
    const initial = getInitialPriceData(product, dataContext, defaultPrintOnly);
    setShownProduct(product);
    setSkuProduct(initial.product);
    setConfiguration(initial);
  }

  // A percentage coupon lowers the shown price, every kind names itself under it.
  const { line: couponLine, price: couponPrice } = couponPricePreview(claimedCoupon.previewSummary, configuration);

  useEffect(() => {
    tracker.productView(skuProduct);
  }, [skuProduct]);

  const handleOnAddToCartClick = () => {
    cartContext.addItem(
      configuration.product,
      configuration.product.version,
      configuration.primaryProductData,
      false,
      prepareBundleData(dataContext, configuration),
    );
  };

  const handleOnStartEditorClick = () => {
    if (configuration.bundle || configuration.upsell) {
      editorContext.createItem(
        configuration.product.id,
        configuration.product.version,
        prepareBundleData(dataContext, configuration),
      );
    } else {
      editorContext.removeBundle(configuration.product.id);
    }
  };

  const handleOnChangeConfiguration = data => {
    if (data.product) {
      setSkuProduct(data.product);
    }
    setConfiguration(data);
  };

  const markupImages = productStillImages(product).map(image =>
    getCloudinaryUrl({ imageKey: image.key, prefix: 'products/', type: 'big', format: 'jpg' }),
  );

  // Shared by the top section and the sticky bar of the redesigned layout.
  const renderBuyAction = () => {
    if (!skuProduct) return null;

    if (skuProduct.display) {
      return (
        <CartPageLink>
          <Button type="primary" block onClick={handleOnAddToCartClick}>
            Add to Cart
          </Button>
        </CartPageLink>
      );
    }

    if (product.config.continueButtonUrl) {
      return (
        <Link
          href={queryString.addParams(product.config.continueButtonUrl, {
            product: product.id,
            ...configuration.config,
          })}
          passHref
          legacyBehavior
        >
          <Button type="primary" block onClick={handleOnStartEditorClick}>
            Continue
          </Button>
        </Link>
      );
    }

    return (
      <EditorPageLink
        productId={skuProduct.id}
        version={skuProduct.version}
        quantity={configuration.quantity}
      >
        <Button type="primary" block onClick={handleOnStartEditorClick}>
          Continue
        </Button>
      </EditorPageLink>
    );
  };
  const buyAction = renderBuyAction();

  const topSection = (
    <Grid.Row>
      <Grid.Col xs={12} lg={7} className="pr-lg-2">
        <ProductDetailCarousel images={product.images} rounded />
      </Grid.Col>

      <Grid.Col xs={12} lg={5} className="pl-lg-2 col-product-data">
        <Typography.H1 className="mb-3">{props.content.caption}</Typography.H1>

        <div className="list-styled mb-4">
          <HtmlContent html={props.content.description} />
        </div>

        {skuProduct ? (
          <>
            <div>
              <ProductConfiguration
                product={product}
                skuProduct={skuProduct}
                onChange={handleOnChangeConfiguration}
                hidden={product.config.hideProductConfiguration}
                defaultPrintOnly={defaultPrintOnly}
                hidePrintOnly={product.config.hidePrintOnly ?? false}
                showSkuDescription={product.config.showSkuDescription}
                labels={product.config.labels}
                optionDescriptions={props.content.optionDescriptions}
              />
              {/* Price label and value are UI text, not content headings — divs keep the heading styling */}
              <div className="h6">
                PRICE{product.config.showSkuPriceStartingAt && ' STARTING AT'}
              </div>
              <ProductPrice
                price={configuration.price}
                discountedPrice={couponPrice}
                currency={product.currency}
                couponLine={couponLine}
              />
            </div>
            {buyAction}
          </>
        ) : props.content.unavailable ? (
          <UnavailableNotice
            unavailable={props.content.unavailable}
            linkedContent={props.linkedContent}
          />
        ) : (
          <Alert type="danger" className="text-center">
            Sorry, this product is not available.
          </Alert>
        )}
      </Grid.Col>
    </Grid.Row>
  );

  return (
    <React.Fragment key={product.id}>
      <Head
        title={props.content.seoTitle ?? props.content.caption}
        fullTitle={props.content.seoFullTitle}
        canonicalPath={props.content.canonicalPath}
        description={props.content.seoDescription ?? props.content.shortDescription}
        image={props.content.seoUrl}
      />

      <ScriptMarkupProduct
        id={product.id}
        url={`${SETTINGS.HOST}${product.url}`}
        name={props.content.caption}
        description={props.content.shortDescription}
        images={markupImages}
        prices={product.prices}
        currency={product.currency}
        brand="Parabo Press"
        host={SETTINGS.HOST}
      />

      {props.content.faq && <ScriptMarkupFaq items={faqContentToItems(props.content.faq)} />}

      {props.breadcrumb && <ScriptMarkupBreadcrumb items={props.breadcrumb} host={SETTINGS.HOST} />}

      <Layout>
        {usesProductPageLayout(props.content) ? (
          <ProductSections
            product={product}
            content={props.content}
            linkedContent={props.linkedContent}
            breadcrumb={props.breadcrumb}
            buyAction={buyAction}
            summary={(configuration.bundle ?? configuration.product)?.name}
            price={
              <ProductPrice
                price={configuration.price}
                discountedPrice={couponPrice}
                currency={product.currency}
              />
            }
          >
            {topSection}
          </ProductSections>
        ) : (
          <LegacyProductSections product={product} skuProduct={skuProduct} content={props.content}>
            {topSection}
          </LegacyProductSections>
        )}
      </Layout>
    </React.Fragment>
  );
};

ProductDetailPage.getInitialProps = async () => {
  throw new Error('Cannot access this page directly. See /[...params] page');
};

ProductDetailPage.propTypes = {
  productId: PropTypes.string.isRequired,
  content: PropTypes.object,
  // Content of pairsWith + related products, loaded for the redesigned layout only.
  linkedContent: PropTypes.arrayOf(PropTypes.object),
  // Home > category > product, set for the redesigned layout only.
  breadcrumb: PropTypes.arrayOf(PropTypes.object),
};

export default ProductDetailPage;
